import {
    AddOnInfo,
    PackageDiscount,
    ProductInfo,
    SelectedPacket
} from 'components/enrollForm/enrollFormTypes'
import { constants, numberFormatter } from 'helpers'
import {
    buildProductListWithAddOns,
    findAddOnForProduct,
    getCatalogPackageDiscount
} from 'helpers/enrollmentHelper'
import { generateUuidv4 } from 'helpers/generateUUIDV4'
import { ClientType } from 'storeTypes'

export type BulkEnrollmentSelectedAddOn = {
    package_id: string
    quantity: number
}

export type BulkEnrollmentSelectedPackage = {
    product_id: string
    pricebook_id: string
    package_id: string
    name: string
    price: string
    size: string
    size_display: string
    unit: string
    image_url: string
    hidePrice: boolean | string
    addOns: AddOnInfo[]
    discount?: PackageDiscount
}

const shouldHideBulkEnrollmentPrice = (hidePrice?: boolean | string) =>
    hidePrice === true || hidePrice === 'True'

export const formatBulkEnrollmentAmount = (
    price: string | number,
    hidePrice?: boolean | string
) => numberFormatter(price, 'CFA', shouldHideBulkEnrollmentPrice(hidePrice))

export const formatBulkEnrollmentPackagePrice = (
    price: string | number,
    hidePrice?: boolean | string
) => `${formatBulkEnrollmentAmount(price, hidePrice)} / unité`

export const getAddOnsForBulkPackage = (
    zoneProducts: ProductInfo[],
    packageId: string
): AddOnInfo[] => findAddOnForProduct(zoneProducts, [packageId])

export const getBulkEnrollmentAddOnQuantity = (
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined,
    packageId: string
) =>
    selectedAddOns?.find((addOn) => addOn.package_id === packageId)?.quantity ||
    0

export const setBulkEnrollmentAddOnQuantity = (
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined,
    packageId: string,
    quantity: number
): BulkEnrollmentSelectedAddOn[] => {
    const nextQuantity = Math.max(
        0,
        Math.min(constants.MAX_ORDER_OF_PACKAGE, Number(quantity) || 0)
    )
    const rest = (selectedAddOns || []).filter(
        (addOn) => addOn.package_id !== packageId
    )

    if (nextQuantity === 0) {
        return rest
    }

    return [...rest, { package_id: packageId, quantity: nextQuantity }]
}

export const flattenEnrollmentCatalogToPackages = (
    zoneProducts: ProductInfo[]
): BulkEnrollmentSelectedPackage[] => {
    const productsWithAddOns = buildProductListWithAddOns(
        zoneProducts,
        {} as ClientType
    )

    return productsWithAddOns
        .filter((product) => !product.isAddOn && product.product_prices?.length)
        .flatMap((product) =>
            product.product_prices
                .filter((productPrice) => !productPrice.depends_on?.length)
                .map((productPrice) => {
                    const discount = getCatalogPackageDiscount(
                        productPrice,
                        product
                    )
                    return {
                        product_id: product.sf_id,
                        pricebook_id: productPrice.pricebook.sf_id,
                        package_id: productPrice.sf_id,
                        name: product.name,
                        price: productPrice.price?.toString(),
                        size: productPrice.package_size?.toString(),
                        size_display: productPrice.package_size_display,
                        unit: product.unit || productPrice.units_of_measure,
                        image_url: product.image_url || '',
                        hidePrice: product.hide_price,
                        addOns: getAddOnsForBulkPackage(
                            zoneProducts,
                            productPrice.sf_id
                        ),
                        ...(discount ? { discount } : {})
                    }
                })
        )
}

export const composeBulkEnrollmentFullname = (
    firstname?: string,
    lastname?: string
) => `${(firstname || '').trim()} ${(lastname || '').trim()}`.trim()

export const getBulkEnrollmentRowNames = (
    client: Pick<ClientType, 'firstname' | 'lastname' | 'fullname'>
) => {
    const firstname = (client.firstname || '').trim()
    const lastname = (client.lastname || '').trim()
    if (firstname || lastname) {
        return { firstname, lastname }
    }

    const parts = (client.fullname || '').trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) {
        return { firstname: '', lastname: '' }
    }
    if (parts.length === 1) {
        return { firstname: parts[0], lastname: '' }
    }

    return { firstname: parts[0], lastname: parts.slice(1).join(' ') }
}

const getAppliedBulkEnrollmentDiscount = (
    discount: PackageDiscount | undefined,
    newQuantity: number,
    existingQuantity = 0
) => {
    if (!discount?.tiers?.length) {
        return { discountedUnits: 0, discountPerPackage: 0 }
    }

    const newQty = Math.max(0, Number(newQuantity) || 0)
    const existing = Math.max(0, Number(existingQuantity) || 0)
    const totalQuantity = existing + newQty
    const matchingTiers = discount.tiers.filter(
        (tier) => totalQuantity >= Number(tier.min_quantity || 0)
    )
    if (!matchingTiers.length) {
        return { discountedUnits: 0, discountPerPackage: 0 }
    }

    const selectedTier = matchingTiers.reduce(
        (best, tier) =>
            Number(tier.min_quantity || 0) > Number(best.min_quantity || 0)
                ? tier
                : best,
        matchingTiers[0]
    )
    const rawCap = discount.max_discounted_quantity
    const hasCap = rawCap !== null && rawCap !== undefined
    const maxDiscounted = hasCap ? Number(rawCap) : NaN
    const remainingSlots = Number.isFinite(maxDiscounted)
        ? Math.max(0, maxDiscounted - existing)
        : newQty
    const discountedUnits = Math.min(newQty, remainingSlots)

    return {
        discountedUnits,
        discountPerPackage: Number(selectedTier.discount_per_package) || 0
    }
}

const netPacketPrice = (
    listPrice: string | number,
    discount: PackageDiscount | undefined,
    newQuantity: number,
    existingQuantity: number,
    unitIndex: number
) => {
    const list = Number(listPrice) || 0
    const { discountedUnits, discountPerPackage } =
        getAppliedBulkEnrollmentDiscount(
            discount,
            newQuantity,
            existingQuantity
        )
    const net =
        unitIndex < discountedUnits ? list - discountPerPackage : list
    return String(Math.max(0, net))
}

export const buildBulkEnrollmentPackets = (
    selectedPackage: BulkEnrollmentSelectedPackage,
    quantity: number,
    existingQuantity = 0
): SelectedPacket[] => {
    const count = Math.max(0, Number(quantity) || 0)

    return Array.from({ length: count }, (_, unitIndex) => ({
        image_url: selectedPackage.image_url,
        product_id: selectedPackage.product_id,
        pricebook_id: selectedPackage.pricebook_id,
        size: selectedPackage.size,
        size_display: selectedPackage.size_display,
        name: selectedPackage.name,
        price: netPacketPrice(
            selectedPackage.price,
            selectedPackage.discount,
            count,
            existingQuantity,
            unitIndex
        ),
        hidePrice: selectedPackage.hidePrice,
        unit: selectedPackage.unit,
        uuid: generateUuidv4(),
        package_id: selectedPackage.package_id,
        isAddOn: false
    }))
}

const toAddOnPacket = (addOn: AddOnInfo, price: string): SelectedPacket => ({
    image_url: addOn.image_url,
    product_id: addOn.product_id,
    pricebook_id: addOn.pricebook_id,
    size: addOn.size?.toString(),
    size_display: addOn.size_display,
    name: addOn.product_name,
    price,
    hidePrice: addOn.hide_price,
    unit: addOn.unit,
    uuid: generateUuidv4(),
    package_id: addOn.package_id,
    isAddOn: true
})

export const buildBulkEnrollmentAddOnPackets = (
    addOns: AddOnInfo[] | undefined,
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined,
    client: ClientType | null | undefined = null
): SelectedPacket[] => {
    const catalog = addOns || []
    const selections = selectedAddOns || []

    return selections.flatMap((selection) => {
        const addOn = catalog.find(
            (item) => item.package_id === selection.package_id
        )
        const count = Math.max(0, Number(selection.quantity) || 0)
        if (!addOn || count < 1) {
            return []
        }

        const existingQuantity = getExistingProductQuantity(
            client,
            addOn.product_id,
            addOn.package_id
        )

        return Array.from({ length: count }, (_, unitIndex) =>
            toAddOnPacket(
                addOn,
                netPacketPrice(
                    addOn.price,
                    addOn.discount,
                    count,
                    existingQuantity,
                    unitIndex
                )
            )
        )
    })
}

export const buildBulkEnrollmentRowPackets = (
    selectedPackage: BulkEnrollmentSelectedPackage,
    quantity: number,
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined = [],
    client: ClientType | null | undefined = null
): SelectedPacket[] => [
    ...buildBulkEnrollmentPackets(
        selectedPackage,
        quantity,
        getExistingProductQuantity(
            client,
            selectedPackage.product_id,
            selectedPackage.package_id
        )
    ),
    ...buildBulkEnrollmentAddOnPackets(
        selectedPackage.addOns,
        selectedAddOns,
        client
    )
]

export const clampBulkEnrollmentPackageQuantity = (quantity: number) =>
    Math.max(
        1,
        Math.min(constants.MAX_ORDER_OF_PACKAGE, Number(quantity) || 1)
    )

const isDroppedOrder = (dropped: unknown) =>
    dropped === true || dropped === 'True' || dropped === 'true'

const getOrderProductId = (order: {
    product?: { id?: string; sf_id?: string }
}) => order.product?.id || order.product?.sf_id || ''

const getOrderPackageId = (order: { package_id?: string }) =>
    order.package_id || ''

export const getExistingProductQuantity = (
    client: ClientType | null | undefined,
    productId: string,
    packageId?: string
) => {
    if (!client?.goal_items?.length || (!productId && !packageId)) return 0

    return client.goal_items.reduce((count, order) => {
        if (isDroppedOrder(order.dropped)) return count
        if (order.season_active === false) return count

        const orderPackageId = getOrderPackageId(order)
        if (packageId && orderPackageId) {
            return orderPackageId === packageId ? count + 1 : count
        }
        if (productId && getOrderProductId(order) === productId) {
            return count + 1
        }
        return count
    }, 0)
}

export const getBulkEnrollmentDiscountAmount = (
    discount: PackageDiscount | undefined,
    newQuantity: number,
    existingQuantity = 0
) => {
    const { discountedUnits, discountPerPackage } =
        getAppliedBulkEnrollmentDiscount(
            discount,
            newQuantity,
            existingQuantity
        )
    return discountedUnits * discountPerPackage
}

export const formatBulkEnrollmentDiscount = (
    amount: number,
    hidePrice?: boolean | string
) => `- ${formatBulkEnrollmentAmount(amount, hidePrice)}`

export const getBulkEnrollmentRowTotal = (
    selectedPackage: BulkEnrollmentSelectedPackage,
    quantity: string | number,
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined = [],
    client: ClientType | null | undefined = null
) => {
    const packageQuantity = Math.max(0, Number(quantity) || 0)
    const packageTotal =
        (Number(selectedPackage.price) || 0) * packageQuantity
    const packageDiscount = getBulkEnrollmentDiscountAmount(
        selectedPackage.discount,
        packageQuantity,
        getExistingProductQuantity(
            client,
            selectedPackage.product_id,
            selectedPackage.package_id
        )
    )
    const addOnTotal = (selectedAddOns || []).reduce((sum, selection) => {
        const addOn = (selectedPackage.addOns || []).find(
            (item) => item.package_id === selection.package_id
        )
        if (!addOn) {
            return sum
        }

        const addOnQuantity = Math.max(0, Number(selection.quantity) || 0)
        const addOnDiscount = getBulkEnrollmentDiscountAmount(
            addOn.discount,
            addOnQuantity,
            getExistingProductQuantity(
                client,
                addOn.product_id,
                addOn.package_id
            )
        )

        return (
            sum +
            (Number(addOn.price) || 0) * addOnQuantity -
            addOnDiscount
        )
    }, 0)

    return packageTotal - packageDiscount + addOnTotal
}

export type BulkEnrollmentThumbnail = {
    key: string
    image_url: string
    name: string
}

export const getBulkEnrollmentRowThumbnails = (
    selectedPackage: BulkEnrollmentSelectedPackage,
    selectedAddOns: BulkEnrollmentSelectedAddOn[] | undefined = []
): BulkEnrollmentThumbnail[] => {
    const thumbnails: BulkEnrollmentThumbnail[] = [
        {
            key: selectedPackage.package_id,
            image_url: selectedPackage.image_url,
            name: selectedPackage.name
        }
    ]

    ;(selectedAddOns || []).forEach((selection) => {
        if (selection.quantity < 1) {
            return
        }

        const addOn = (selectedPackage.addOns || []).find(
            (item) => item.package_id === selection.package_id
        )
        if (!addOn) {
            return
        }

        thumbnails.push({
            key: addOn.package_id,
            image_url: addOn.image_url,
            name: addOn.product_name
        })
    })

    return thumbnails
}
